import { appConfig } from "@common-config/app.config";
/**
 * Web-app side of the consent contract (mirrors apps/blueticks/src/lib/consent/loaders.ts).
 * Nothing here runs for a visitor without a current stored choice. Vendor scripts are loaded
 * by inject-pixels.tsx, which calls these helpers; this module owns Consent Mode and logging.
 */
import {
  consentLogUrl,
  isLoggableOrigin,
  CONSENT_MODE_DEFAULTS,
  buildConsentLogPayload,
  consentLogDomain,
  consentModeUpdate,
  type ConsentChoices,
  type ConsentLogGeo,
  type ConsentLogPayload,
  type ConsentSource,
} from "./contract";
import { CONSENT_CHANGE_EVENT, clearTrackerData } from "./store";

type GtagFn = (...args: unknown[]) => void;
type ConsentWindow = { dataLayer?: unknown[]; gtag?: GtagFn; __btConsentDefaults?: boolean };

const cw = (): ConsentWindow => window as unknown as ConsentWindow;

const loadedVendors = new Set<string>();

/** Marks a vendor as injected. Returns false if it was already injected on this page. */
export function markVendorLoaded(name: string): boolean {
  if (loadedVendors.has(name)) return false;
  loadedVendors.add(name);
  return true;
}

/** True once any third-party vendor has been injected on this page (withdrawal then needs a reload). */
export function anyVendorLoaded(): boolean {
  return loadedVendors.size > 0;
}

/**
 * Consent Mode defaults must precede any Google tag. public/index.html pushes them first and sets
 * window.__btConsentDefaults; this fallback creates the same stub so a late call never pushes
 * raw arrays to dataLayer.
 */
export function ensureGtag(): GtagFn {
  if (typeof window === "undefined") return () => undefined;
  const w = cw();
  w.dataLayer = w.dataLayer || [];
  if (!w.gtag) {
    w.gtag = function gtagShim() {
      // gtag.js needs the Arguments object itself, not an array.
      // eslint-disable-next-line prefer-rest-params
      w.dataLayer!.push(arguments);
    };
  }
  if (!w.__btConsentDefaults) {
    w.gtag("consent", "default", { ...CONSENT_MODE_DEFAULTS });
    w.__btConsentDefaults = true;
  }
  return w.gtag!;
}

/** Pushes the Consent Mode update for the given choices (safe to call repeatedly). */
export function updateConsentMode(choices: ConsentChoices): void {
  if (typeof window === "undefined") return;
  ensureGtag()("consent", "update", consentModeUpdate(choices));
}

/**
 * Denies everything in Consent Mode and removes tracker cookies and storage we can see.
 * The caller reloads the page if a vendor was live, so third-party scripts unload.
 */
export function withdrawTrackers(): string[] {
  updateConsentMode({ analytics: false, marketing: false, functional: true });
  return typeof window === "undefined" ? [] : clearTrackerData(window.location.hostname);
}

/** Subscribes to same-tab saves and to other tabs' saves (storage event). Returns an unsubscribe. */
export function onConsentChange(callback: () => void): () => void {
  if (typeof window === "undefined" || typeof window.addEventListener !== "function") return () => undefined;
  const onStorage = (e: StorageEvent) => {
    if (e.key === null || e.key === "bt_consent") callback();
  };
  window.addEventListener(CONSENT_CHANGE_EVENT, callback);
  window.addEventListener("storage", onStorage);
  return () => {
    window.removeEventListener(CONSENT_CHANGE_EVENT, callback);
    window.removeEventListener("storage", onStorage);
  };
}

/** Fire-and-forget evidence write (spec section 6). Never blocks the UI and never throws. */
export function postConsentLog(input: {
  consentId: string;
  choices: ConsentChoices;
  source: ConsentSource;
  userId?: string;
  geo?: ConsentLogGeo;
}): void {
  if (typeof window === "undefined" || typeof fetch !== "function") return;
  const apiBase = appConfig.API_ENDPOINT;
  const domain = consentLogDomain(window.location.hostname);
  if (!domain || !isLoggableOrigin(window.location.hostname, apiBase)) return; // localhost, preview hosts and staging-to-prod are not logged.
  try {
    const payload: ConsentLogPayload & { userId?: string } = buildConsentLogPayload({
      consentId: input.consentId,
      choices: input.choices,
      source: input.source,
      domain,
      geo: input.geo,
    });
    // userId is client-asserted and only present when signed in (coordinator contract).
    if (input.userId) payload.userId = input.userId;
    void fetch(consentLogUrl(apiBase), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      keepalive: true,
      credentials: "omit",
    }).catch(() => undefined);
  } catch {
    /* Logging must never break the banner. */
  }
}
